"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const AssertionError_1 = require("../error/AssertionError");
const EtudeError_1 = require("../error/EtudeError");
const EtudeParser_1 = require("../util/EtudeParser");
const Exceptional_1 = require("../util/Exceptional");
const ImmutablePrioritySet_1 = require("../util/ImmutablePrioritySet");
const MathUtil_1 = require("../util/MathUtil");
const StreamUtil_1 = require("../util/StreamUtil");
const Accidental_1 = require("./Accidental");
const Letter_1 = require("./Letter");
const MusicConstants_1 = require("./MusicConstants");
const Policy_1 = require("./Policy");
class Key {
    constructor(letter, parameter = Exceptional_1.default.empty()) {
        this.letter = letter;
        if (parameter instanceof Accidental_1.default) {
            this.accidental = Exceptional_1.default.of(parameter);
        }
        else if (parameter instanceof Exceptional_1.default) {
            this.accidental = parameter;
        }
        else {
            this.accidental = Exceptional_1.default.empty();
        }
    }
    step(amount, policies = Policy_1.default.DEFAULT_PRIORITY) {
        return Key.fromOffset(MathUtil_1.default.floorMod(this.getOffset() + amount, MusicConstants_1.default.KEYS_IN_OCTAVE), policies);
    }
    apply(keySignature) {
        const accidental = keySignature.getAccidentalFor(this.letter);
        if (this.accidental === accidental) {
            return this;
        }
        return new Key(this.letter, accidental);
    }
    removeAccidental() {
        if (!this.hasAccidental()) {
            return this;
        }
        return new Key(this.letter);
    }
    natural() {
        if (this.isNatural()) {
            return this;
        }
        return new Key(this.letter, Accidental_1.default.NATURAL);
    }
    sharp() {
        if (this.isSharp()) {
            return this;
        }
        return new Key(this.letter, Accidental_1.default.SHARP);
    }
    doubleSharp() {
        if (this.isDoubleSharp()) {
            return this;
        }
        return new Key(this.letter, Accidental_1.default.DOUBLE_SHARP);
    }
    tripleSharp() {
        if (this.isTripleSharp()) {
            return this;
        }
        return new Key(this.letter, Accidental_1.default.TRIPLE_SHARP);
    }
    flat() {
        if (this.isFlat()) {
            return this;
        }
        return new Key(this.letter, Accidental_1.default.FLAT);
    }
    doubleFlat() {
        if (this.isDoubleFlat()) {
            return this;
        }
        return new Key(this.letter, Accidental_1.default.DOUBLE_FLAT);
    }
    tripleFlat() {
        if (this.isTripleFlat()) {
            return this;
        }
        return new Key(this.letter, Accidental_1.default.TRIPLE_FLAT);
    }
    hasAccidental() {
        return this.accidental.isPresent();
    }
    isNatural() {
        return this.accidental.orElse(null) === Accidental_1.default.NATURAL;
    }
    isSharp() {
        return this.accidental.orElse(null) === Accidental_1.default.SHARP;
    }
    isDoubleSharp() {
        return this.accidental.orElse(null) === Accidental_1.default.DOUBLE_SHARP;
    }
    isTripleSharp() {
        return this.accidental.orElse(null) === Accidental_1.default.TRIPLE_SHARP;
    }
    isFlat() {
        return this.accidental.orElse(null) === Accidental_1.default.FLAT;
    }
    isDoubleFlat() {
        return this.accidental.orElse(null) === Accidental_1.default.DOUBLE_FLAT;
    }
    isTripleFlat() {
        return this.accidental.orElse(null) === Accidental_1.default.TRIPLE_FLAT;
    }
    getEnharmonicEquivalent(parameter) {
        if (parameter instanceof Letter_1.default) {
            const letter = parameter;
            return Exceptional_1.default
                .ofNullable(letter)
                .flatMap(l => {
                if (this.letter === l) {
                    return Exceptional_1.default.of(this);
                }
                const targetOffset = this.getOffset();
                const initialOffset = letter.getOffset();
                let accidentalOffset = targetOffset - initialOffset;
                // assuming key is above, check if some sharp can offset enough
                if (accidentalOffset > Accidental_1.default.TRIPLE_SHARP.getOffset()) {
                    // assumption was wrong, now assume key is below
                    accidentalOffset -= MusicConstants_1.default.KEYS_IN_OCTAVE;
                    // check if some flat can offset enough
                    if (accidentalOffset < Accidental_1.default.TRIPLE_FLAT.getOffset()) {
                        return Exceptional_1.default.empty(EtudeError_1.default.forInvalid(Key, accidentalOffset));
                    }
                }
                else if (accidentalOffset < Accidental_1.default.TRIPLE_FLAT.getOffset()) {
                    accidentalOffset += MusicConstants_1.default.KEYS_IN_OCTAVE;
                    if (accidentalOffset > Accidental_1.default.TRIPLE_SHARP.getOffset()) {
                        return Exceptional_1.default.empty(EtudeError_1.default.forInvalid(Key, accidentalOffset));
                    }
                }
                const accidental = Accidental_1.default.fromOffset(accidentalOffset);
                return Exceptional_1.default.of(new Key(letter, accidental));
            });
        }
        else if (parameter instanceof ImmutablePrioritySet_1.default) {
            const policies = parameter;
            // all keys that are enharmonic equivalents
            const keys = Letter_1.default
                .stream()
                .limit(Letter_1.default.size)
                .map(l => this.getEnharmonicEquivalent(l))
                .filter(e => e.isPresent())
                .map(e => e.get());
            // find the first policy that tests true for some enharmonic key and return that key
            const value = StreamUtil_1.default
                .fromIterator(policies.iterator())
                .map(policy => {
                const value = keys.find(k => policy(k));
                return Exceptional_1.default.ofNullable(value);
            })
                .filter(e => e.isPresent())
                .map(e => e.get())
                .limit(1)[0];
            return Exceptional_1.default
                .ofNullable(value)
                .withException(EtudeError_1.default.forInvalid(Key, "unable to find an enharmonic equivalent for the given policies"));
        }
    }
    static isEnharmonic(a, b) {
        return a.getOffset() === b.getOffset();
    }
    static fromOffset(offset, policies = Policy_1.default.DEFAULT_PRIORITY) {
        if (policies.size === 0) {
            throw EtudeError_1.default.forIllegalArgument(Key, policies, "should not be empty");
        }
        let letter;
        let accidental = null;
        // determine key without taking into account policies
        // maintain order of cases for fall throughs to function correctly
        switch (MathUtil_1.default.floorMod(offset, MusicConstants_1.default.KEYS_IN_OCTAVE)) {
            case 11:
                letter = Letter_1.default.B;
                break;
            case 10:
                accidental = Accidental_1.default.SHARP;
            // fall through
            case 9:
                letter = Letter_1.default.A;
                break;
            case 8:
                accidental = Accidental_1.default.SHARP;
            // fall through
            case 7:
                letter = Letter_1.default.G;
                break;
            case 6:
                accidental = Accidental_1.default.SHARP;
            // fall through
            case 5:
                letter = Letter_1.default.F;
                break;
            case 4:
                letter = Letter_1.default.E;
                break;
            case 3:
                accidental = Accidental_1.default.SHARP;
            // fall through
            case 2:
                letter = Letter_1.default.D;
                break;
            case 1:
                accidental = Accidental_1.default.SHARP;
            // fall through
            case 0:
                letter = Letter_1.default.C;
                break;
            default:
                throw new AssertionError_1.default("Invalid offset: " + offset);
        }
        return new Key(letter, accidental).getEnharmonicEquivalent(policies);
    }
    getOffset() {
        return MathUtil_1.default.floorMod(this.letter.getOffset() + this.accidental.map(a => a.getOffset()).orElse(0), MusicConstants_1.default.KEYS_IN_OCTAVE);
    }
    static fromString(keyString) {
        return EtudeParser_1.default
            .of(keyString)
            .filter(o => o != null, EtudeError_1.default.forNull(Key))
            .filter(s => s.trim().length !== 0, EtudeError_1.default.forInvalid(Key, keyString, "empty string"))
            .parse(s => Letter_1.default.fromChar(s.charAt(0)))
            .parse(s => Exceptional_1.default.of(Accidental_1.default.fromString(s.length === 1 ? null : s.substring(1))))
            .get(a => new Key(a[0], a[1]));
    }
    toString() {
        return this.letter.toString() + this.accidental.map(a => a.toString()).orElse("");
    }
    equals(other) {
        if (!(other instanceof Key)) {
            return false;
        }
        if (other === this) {
            return true;
        }
        let otherKey = other;
        return this.letter === otherKey.getLetter() && this.accidental === otherKey.getAccidental();
    }
    getLetter() {
        return this.letter;
    }
    getAccidental() {
        return this.accidental;
    }
}
exports.default = Key;
